import React, { useState } from 'react'
import Card from '@material-ui/core/Card'
import CardContent from '@material-ui/core/CardContent'
import Typography from '@material-ui/core/Typography'
import Box from '@material-ui/core/Box'
import Chip from '@material-ui/core/Chip'
import Tooltip from '@material-ui/core/Tooltip'
import clsx from 'clsx'
import format from 'date-fns/format'
import pluralize from 'lib/pluralize'
import RedeemIcon from 'components/material-symbol/icons/Redeem'
import LocationOnOutlinedIcon from 'components/material-symbol/icons/LocationOn'
import ScheduleOutlinedIcon from 'components/material-symbol/icons/Schedule'
import PersonOutlineIcon from 'components/material-symbol/icons/PersonOutline'
import CollectionsBookmarkOutlinedIcon from 'components/material-symbol/icons/CollectionsBookmark'
import { toFullTextDayDateWithTimezone } from 'utils/dateUtils'
import { durationText } from 'utils/durationUtils'
import LessonPackCardActions from './lesson-pack-card-actions'
import ConsumerLessonPackDetailsDialog from './consumer-lesson-pack-details-dialog'
import { useStyles } from './styles'

const IconBadge = ({ label, className, children }) => (
  <Tooltip title={label}>
    <Box
      component="span"
      className={clsx(className)}
      aria-label={label}
      role="img"
    >
      {children}
    </Box>
  </Tooltip>
)

const LessonPackCard = ({
  expiresAt,
  totalCredits,
  remainingCredits,
  lessonCredits,
  lessonType,
  isUsedOrExpired,
  id: lessonPackId,
  isGifted = false,
  onGiftClick,
}) => {
  const classes = useStyles()
  const [isDetailsOpen, setIsDetailsOpen] = useState(false)
  const { title, coach, giftingEnabled, locations = [], durationInMinutes } =
    lessonType || {}
  const isGiftable =
    giftingEnabled && Number(totalCredits) === Number(remainingCredits)
  const { name } = coach || {}
  const locationName = locations[0]?.name

  const formattedLastUpdatedAtDate = lastUpdAt => {
    if (!lastUpdAt) return null
    return format(lastUpdAt, 'MMMM d, yyyy')
  }

  const defineLastUpdatedDate = credits => {
    if (!credits?.length) return null
    return Math.max(...credits.map(item => new Date(item.updatedAt)))
  }

  let lastUpdatedAt
  if (isUsedOrExpired) {
    lastUpdatedAt = formattedLastUpdatedAtDate(
      defineLastUpdatedDate(lessonCredits),
    )
  }

  const handleCardClick = () => {
    setIsDetailsOpen(true)
  }

  const handleCardKeyDown = event => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      handleCardClick()
    }
  }

  const lessonPackDetails = {
    id: lessonPackId,
    expiresAt,
    totalCredits,
    remainingCredits,
    lessonCredits,
    lessonType,
    isUsedOrExpired,
    isGifted,
  }

  return (
    <Card elevation={0} className={classes.card}>
      <CardContent className={classes.cardContent}>
        <Box className={classes.cardInner}>
          <Box
            className={classes.cardDetails}
            onClick={handleCardClick}
            role="button"
            tabIndex={0}
            onKeyDown={handleCardKeyDown}
          >
            <Box className={classes.badgeRow}>
              <IconBadge
                label="Lesson Pack"
                className={clsx(classes.iconBadge, classes.chipLessonPack)}
              >
                <CollectionsBookmarkOutlinedIcon />
              </IconBadge>
              {isGiftable ? (
                <IconBadge
                  label="Giftable"
                  className={clsx(classes.iconBadge, classes.chipGiftable)}
                >
                  <RedeemIcon />
                </IconBadge>
              ) : null}
              {isGifted ? (
                <Chip
                  icon={
                    <RedeemIcon fontSize="small" opticalSize={14} aria-hidden />
                  }
                  label="Gifted"
                  className={clsx(classes.chip, classes.chipGifted)}
                />
              ) : null}
              {remainingCredits > 0 ? (
                <Chip
                  label={`${remainingCredits}/${totalCredits} Remaining`}
                  className={clsx(classes.chip, classes.chipRemaining)}
                />
              ) : null}
            </Box>

            <Typography
              component="h3"
              variant="subtitle1"
              className={classes.cardTitle}
            >
              {title}
            </Typography>

            <Box className={classes.metadataRow}>
              {name && (
                <Box className={classes.metadataItem}>
                  <PersonOutlineIcon className={classes.metadataIcon} />
                  <Typography
                    variant="caption"
                    className={classes.metadataText}
                    noWrap
                    title={name}
                  >
                    {name}
                  </Typography>
                </Box>
              )}
              {locationName && (
                <Box className={classes.metadataItem}>
                  <LocationOnOutlinedIcon className={classes.metadataIcon} />
                  <Typography
                    variant="caption"
                    className={classes.metadataText}
                    noWrap
                    title={locationName}
                  >
                    {locationName}
                  </Typography>
                </Box>
              )}
              {durationInMinutes ? (
                <Box className={classes.metadataItem}>
                  <ScheduleOutlinedIcon className={classes.metadataIcon} />
                  <Typography
                    variant="caption"
                    className={classes.metadataText}
                  >
                    {durationText(durationInMinutes)}
                  </Typography>
                </Box>
              ) : null}
              {isUsedOrExpired && (
                <Typography variant="caption" className={classes.metadataText}>
                  {totalCredits} {pluralize(totalCredits, 'lesson')}
                  {lastUpdatedAt ? ` · Last used ${lastUpdatedAt}` : ''}
                </Typography>
              )}
            </Box>

            <Typography
              variant="caption"
              className={classes.metadataText}
              display="block"
            >
              {expiresAt
                ? `Expires ${toFullTextDayDateWithTimezone(expiresAt)}`
                : 'Does not expire'}
            </Typography>
          </Box>

          {!isUsedOrExpired && (
            <Box className={classes.cardActions}>
              <LessonPackCardActions
                isGiftable={isGiftable}
                onGiftClick={onGiftClick}
                lessonType={lessonType}
                remainingCredits={remainingCredits}
                lessonPackId={lessonPackId}
                totalCredits={totalCredits}
                isUsedOrExpired={isUsedOrExpired}
              />
            </Box>
          )}
        </Box>
      </CardContent>
      <ConsumerLessonPackDetailsDialog
        isOpen={isDetailsOpen}
        setIsOpen={setIsDetailsOpen}
        lessonPack={lessonPackDetails}
        onGiftClick={onGiftClick}
      />
    </Card>
  )
}

export default LessonPackCard
